import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useState } from 'react';
import { dsClass } from '../../common/constants/dsClasses';
import { activateMsg, closeMsg, discountVoucherMsg, t } from '../../common/i18n';
import { Input } from '../Input/Input';
import type { ChangeEvent, FormEvent } from 'react';

import './Voucher.scss';

export interface VoucherProps {
  onSubmitVoucher: (voucherCode: string) => Promise<void>;
  clearVoucher: () => void;
  voucherError: boolean;
  clearError: () => void;
  selectedVoucherCode: string;
  isLoadingModels: boolean;
  compact?: boolean;
  hideCampaigns?: boolean;
}

interface VoucherTextProps {
  text: string;
  onDisableClick: () => void;
}

const VoucherText = ({ text, onDisableClick }: VoucherTextProps) => (
  <div className={`of-voucher-text ${dsClass.BACKGROUND_COLOR_GREEN_100} ${dsClass.PADDING_2}`}>
    <span className={dsClass.MARGIN_RIGHT_1}>{text}</span>
    <CL.Button size="s" color="link" onClick={onDisableClick} aria-label={t.WOYD(closeMsg)}>
      <CL.Icon icon="close" color="blue-800" />
    </CL.Button>
  </div>
);

export const Voucher = ({
  onSubmitVoucher,
  clearVoucher,
  voucherError,
  clearError,
  selectedVoucherCode,
  isLoadingModels,
  compact,
  hideCampaigns,
}: VoucherProps) => {
  const [voucherCode, setVoucherCode] = useState(selectedVoucherCode);
  const [contractualError, setContractualError] = useState(false);

  const onValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    setVoucherCode(e.target.value);
    setContractualError(false);
    if (voucherError) {
      clearError();
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (hideCampaigns) {
      if (voucherCode) {
        setContractualError(true);
      }
      return;
    }
    await onSubmitVoucher(voucherCode);
  };

  const handleClear = () => {
    setVoucherCode('');
    clearVoucher();
  };

  const getErrorMessage = () => {
    if (contractualError) {
      return t.G0UE('Discount codes cannot be used due to contractual obligations.');
    }
    if (voucherError) {
      return t.QQGU('Invalid discount code.');
    }
    return undefined;
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div
        className={classNames(
          'of-voucher-wrapper',
          dsClass.JUSTIFY_CONTENT_FLEX_START,
          dsClass.PADDING_TOP_4,
          !compact && dsClass.MARGIN_LEFT_5,
          !compact && dsClass.MARGIN_RIGHT_5
        )}
      >
        <div className="input-section">
          <Input
            label={t.GOH2(discountVoucherMsg)}
            type="text"
            id="voucherCode"
            name="voucherCode"
            onChange={onValueChange}
            value={voucherCode}
            error={getErrorMessage()}
            disabled={isLoadingModels}
            placeholder={t.DODI('Enter discount code')}
          />
          <CL.Button
            className={`${dsClass.MARGIN_LEFT_3} ${dsClass.PADDING_HORIZONTAL_5} ${dsClass.MARGIN_TOP_1} ${dsClass.MARGIN_BOTTOM_0}`}
            loading={isLoadingModels}
            id="voucherButton"
            i18n_button_loading_ariaText={t.WFEF('Checking discount code {}', voucherCode)}
            type="submit"
            color="light"
            disabled={isLoadingModels || voucherCode.length === 0}
          >
            {t.EITS(activateMsg)}
          </CL.Button>
        </div>
        <div className={dsClass.DISPLAY_INLINE_BLOCK}>
          {selectedVoucherCode && (
            <VoucherText
              text={t.QK7P('Discount code {} activated.', selectedVoucherCode)}
              onDisableClick={handleClear}
            />
          )}
        </div>
      </div>
    </form>
  );
};
